import { useTranslation } from 'react-i18next';

import { Field, Input, SegmentedButtons, Textarea } from '@/shared/ui';
import { SERIE_COST_CURRENCY_OPTIONS, SERIE_TRANSPORT_OPTIONS } from '../serieDetailFormSchema';
import type { SerieDetailFormApi } from '../useSerieDetailForm';

interface SerieBasicsFormProps {
  form: SerieDetailFormApi;
  /** Read-only rozsah seriálu (odvozený z termínů). */
  dateRange: string;
  /** Délka je zamčená, jakmile seriál obsahuje termíny (termín má stejnou délku jako seriál). */
  lengthLocked: boolean;
  /** Typ dopravy je zamčený po schválení vrstvy Termíny. */
  transportLocked: boolean;
}

function NumberSuffixInput({
  value,
  onChange,
  onBlur,
  invalid,
  suffix,
  disabled,
  title,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  invalid?: boolean;
  suffix: string;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <span className="relative inline-flex w-[78px]">
      <Input
        type="number"
        min={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        invalid={invalid}
        disabled={disabled}
        title={title}
        className="pr-8"
      />
      <span className="text-phx-xs text-phx-text-muted pointer-events-none absolute top-1/2 right-[9px] -translate-y-1/2">
        {suffix}
      </span>
    </span>
  );
}

/**
 * Formulář „Údaje o termínovém seriálu“ (schválená varianta labely vlevo) nad TanStack Form.
 * Hodnoty i validaci drží `useSerieDetailForm`; tento komponent jen renderuje pole.
 */
export function SerieBasicsForm({
  form,
  dateRange,
  lengthLocked,
  transportLocked,
}: SerieBasicsFormProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <div className="gap-x-phx-7 gap-y-phx-3 grid max-w-5xl grid-cols-1 md:grid-cols-2">
      <form.Field name="name">
        {(field) => {
          const error = field.state.meta.isTouched
            ? (field.state.meta.errors[0]?.message as string | undefined)
            : undefined;
          return (
            <Field layout="inline" label={t('serieDetail.basics.name')} required error={error}>
              <Input
                value={field.state.value}
                onChange={(event) => field.handleChange(event.target.value)}
                onBlur={field.handleBlur}
                required
                invalid={Boolean(error)}
              />
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="transportType">
        {(field) => (
          <Field layout="inline" label={t('serieDetail.basics.transport')}>
            <SegmentedButtons
              value={field.state.value as (typeof SERIE_TRANSPORT_OPTIONS)[number]}
              onChange={(next) => field.handleChange(next)}
              options={SERIE_TRANSPORT_OPTIONS.map((option) => ({
                value: option,
                label: t(`serie.transport.${option}`),
              }))}
              disabled={transportLocked}
              title={transportLocked ? t('serieDetail.basics.transportLockedHint') : undefined}
            />
          </Field>
        )}
      </form.Field>

      <form.Field name="capacityPerTerm">
        {(capacityField) => (
          <form.Field name="mainProgramLengthDays">
            {(lengthField) => {
              const capacityError = capacityField.state.meta.isTouched
                ? (capacityField.state.meta.errors[0]?.message as string | undefined)
                : undefined;
              const lengthError = lengthField.state.meta.isTouched
                ? (lengthField.state.meta.errors[0]?.message as string | undefined)
                : undefined;
              return (
                <Field
                  layout="inline"
                  label={t('serieDetail.basics.capacityPerTerm')}
                  error={capacityError ?? lengthError}
                >
                  <div className="gap-phx-2 flex items-center">
                    <NumberSuffixInput
                      value={capacityField.state.value}
                      onChange={capacityField.handleChange}
                      onBlur={capacityField.handleBlur}
                      invalid={Boolean(capacityError)}
                      suffix={t('serieDetail.basics.paxSuffix')}
                    />
                    <span className="text-phx-sm text-phx-text-2 ml-1 font-medium whitespace-nowrap">
                      {t('serieDetail.basics.length')}
                    </span>
                    <NumberSuffixInput
                      value={lengthField.state.value}
                      onChange={lengthField.handleChange}
                      onBlur={lengthField.handleBlur}
                      invalid={Boolean(lengthError)}
                      suffix={t('serieDetail.basics.daysSuffix')}
                      disabled={lengthLocked}
                      title={lengthLocked ? t('serieDetail.basics.lengthLockedHint') : undefined}
                    />
                  </div>
                </Field>
              );
            }}
          </form.Field>
        )}
      </form.Field>

      <Field layout="inline" label={t('serieDetail.basics.range')}>
        <div className="text-phx-md text-phx-text py-phx-2_5 tabular-nums">{dateRange}</div>
      </Field>

      <form.Field name="defaultCostCurrency">
        {(field) => (
          <Field layout="inline" label={t('serieDetail.basics.defaultCostCurrency')}>
            <select
              value={field.state.value}
              onChange={(event) =>
                field.handleChange(
                  event.target.value as (typeof SERIE_COST_CURRENCY_OPTIONS)[number]
                )
              }
              onBlur={field.handleBlur}
              className="border-phx-border bg-phx-surface text-phx-text text-phx-sm rounded-phx h-7.5 w-30 border px-2"
            >
              {SERIE_COST_CURRENCY_OPTIONS.map((option) => (
                <option key={option || 'none'} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </Field>
        )}
      </form.Field>

      <form.Field name="note">
        {(field) => (
          <Field layout="inline" label={t('serieDetail.basics.note')} className="md:col-span-2">
            <Textarea
              rows={2}
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
            />
          </Field>
        )}
      </form.Field>
    </div>
  );
}
